import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Feedback } from '../../models/feedback/Feedback'
import { FeedbackState } from '../../models/store/feedback/FeedbackState'
import { FileInfo } from '../../services/file-service'
import {
  getFeedbackConversation,
  getFeedbackHighlight,
  saveFeedback,
  getFeedback,
  SaveFeedbackProps,
} from '../../services/feedback-service'
import { FeedbackUpload } from '../../models/feedback'
import { RootState } from 'store/store'

type FetchFeedbackParams = {
  unreadOnly: boolean
  eventId: string
  skip?: number
  limit?: number
  bpId?: string
}

export const fetchFeedbackAction = createAsyncThunk<
  any,
  FetchFeedbackParams,
  { state: RootState }
>(
  'feedback/fetchFeedback',
  async (
    data: FetchFeedbackParams,
    { getState }: { getState: () => RootState },
  ) => {
    const { unreadOnly, eventId, skip = 0, limit = 100, bpId } = data
    return await getFeedback(
      bpId ?? getState().auth.partnerId,
      unreadOnly,
      skip,
      limit,
      eventId,
    )
  },
)

export const fetchFeedbackConversationAction = createAsyncThunk(
  'feedback/fetchFeedbackConversation',
  async (data: { vendorObjectId; itemBpId; parentItemBpId; userType }) => {
    const { vendorObjectId, itemBpId, parentItemBpId, userType } = data
    return await getFeedbackConversation({
      vendorObjectId,
      itemBpId,
      parentItemBpId,
      userType,
    })
  },
)

export const fetchFeedbackHighlightAction = createAsyncThunk(
  'feedback/fetchFeedbackHighlight',
  async (data: { eventId; bidObjectId; userType }) => {
    const { eventId, bidObjectId, userType } = data
    return await getFeedbackHighlight(eventId, bidObjectId, userType)
  },
)

export const saveFeedbackAction = createAsyncThunk(
  'feedback/saveFeedback',
  async (args: SaveFeedbackProps) => {
    return await saveFeedback(args)
  },
)

export const saveGeneralFeedbackAction = createAsyncThunk(
  'feedback/saveGeneralFeedback',
  async (args: Omit<SaveFeedbackProps, 'itemBpId'>) => {
    return saveFeedback({
      ...args,
      itemBpId: '0',
    })
  },
)

const initialState: FeedbackState = {
  feedback: {},
  isFetchingFeedback: false,
  feedbackConversation: [],
  isFetchingFeedbackConversation: false,
  feedbackHighlights: {},
  isFetchingFeedbackHighlight: false,
  totalUnreadFeedbackCount: null,
}

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    addFeedbackFileAction: (state, action) => {
      const payload: FeedbackUpload = action.payload
      state.feedbackConversation = [...state.feedbackConversation, payload]
    },
    updateFeedbackFileAction: (state, action) => {
      const payload: FileInfo = action.payload
      const updatedState = state.feedbackConversation.map(conversation => {
        if (
          conversation._type === 'upload' &&
          conversation.fileInfo.fileName === payload.fileName
        ) {
          return {
            ...conversation,
            fileInfo: payload,
          }
        } else {
          return conversation
        }
      })

      state.feedbackConversation = updatedState
    },
  },
  extraReducers: builder => {
    builder

      // fetchFeedbackConversationAction
      .addCase(fetchFeedbackConversationAction.pending, state => {
        state.isFetchingFeedbackConversation = true
      })
      .addCase(
        fetchFeedbackConversationAction.fulfilled,
        (state, action: { type?: string; payload: any }) => {
          const { payload } = action

          const conversation: Feedback[] = payload.data.map(feedback => {
            if (feedback.comments === undefined) {
              return {
                _type: 'upload',
                ...feedback,
              }
            } else if ((feedback.fileId ?? null) !== null) {
              return {
                _type: 'file',
                ...feedback,
              }
            } else {
              return {
                _type: 'comment',
                ...feedback,
              }
            }
          })

          state.isFetchingFeedbackConversation = false
          state.feedbackConversation = conversation
        },
      )

      // fetchFeedbackAction
      .addCase(fetchFeedbackAction.pending, state => {
        state.isFetchingFeedback = true
      })
      .addCase(fetchFeedbackAction.fulfilled, (state, action) => {
        const { payload } = action
        const { unreadOnly, eventId, bpId } = action.meta.arg

        if (unreadOnly && (!eventId || !bpId) && payload && payload.data) {
          state.totalUnreadFeedbackCount = payload.data.messageCount
        }

        state.isFetchingFeedback = false
        state.feedback = payload?.data
      })
      .addCase(fetchFeedbackAction.rejected, state => {
        state.isFetchingFeedback = false
      })

      // fetchFeedbackHighlightAction
      .addCase(fetchFeedbackHighlightAction.pending, state => {
        state.isFetchingFeedbackHighlight = true
      })
      .addCase(fetchFeedbackHighlightAction.fulfilled, (state, action) => {
        const { payload } = action

        const value =
          payload && payload.data && payload.data.feedbackHighlights
            ? { ...payload.data.feedbackHighlights }
            : {}

        const feedbackHighlights = state.feedbackHighlights
        feedbackHighlights[action.meta.arg.bidObjectId] = value

        state.feedbackHighlights = feedbackHighlights
        state.isFetchingFeedbackHighlight = false
      })
  },
})

export const { addFeedbackFileAction, updateFeedbackFileAction } =
  feedbackSlice.actions

export default feedbackSlice.reducer
